import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui';
import { Button } from '@/components/ui/button';
import TooltipWrapper from '@/components/ui/tooltip';
import useUpdateOrders, {
  CreateOrderInputSchemaWithId,
} from '@/features/account/api/update-orders';
import EditTpslInput from '@/features/trade/order/components/edit-tpsl-input';
import { validateFinalLimitPrice } from '@/features/trade/order/utils/helpers';
import {
  getIsMigrationTradingBlocked,
  getMigrationBlockButtonKey,
  useMigrationTradingBlock,
} from '@/hooks/use-migration';
import {
  getIsWeekendTradingBlocked,
  useWeekendTrading,
} from '@/hooks/use-weekend-trading';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { OrderType } from '@/types/enums';
import { cn } from '@/utils/cn';
import { abbrFromSym } from '@/utils/token-symbol';
import {
  bigIntToDecimalStr,
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { zodResolver } from '@hookform/resolvers/zod';
import { DialogProps } from '@radix-ui/react-dialog';
import { MinusCircledIcon, PlusCircledIcon } from '@radix-ui/react-icons';
import { Edit2 } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

interface TpslEditProps {
  symbol: string;
  disabled?: boolean;
}

const formSchema = z.object({
  tps: z.array(CreateOrderInputSchemaWithId),
  sls: z.array(CreateOrderInputSchemaWithId),
});

export type TpslEditFormSchema = z.infer<typeof formSchema>;

const LazyTPSLEditContent = lazy(() =>
  Promise.resolve({
    default: TPSLEditContent,
  }),
);

export default function TpSlEdit({ symbol, disabled }: TpslEditProps) {
  const [open, setOpen] = useState(false);
  const { isBlocked: isMigrationBlocked } = useMigrationTradingBlock();

  useEffect(() => {
    if (isMigrationBlocked) setOpen(false);
  }, [isMigrationBlocked]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger disabled={disabled || isMigrationBlocked}>
        <Edit2
          size={14}
          className="text-muted-foreground hover:text-foreground"
        />
      </DialogTrigger>
      {open && (
        <Suspense fallback={null}>
          <LazyTPSLEditContent
            symbol={symbol}
            open={open}
            onOpenChange={setOpen}
          />
        </Suspense>
      )}
    </Dialog>
  );
}

type TpslEditContentProps = Pick<TpslEditProps, 'symbol'> & DialogProps;

function TPSLEditContent({ symbol, onOpenChange }: TpslEditContentProps) {
  const { t } = useTranslation();

  const form = useForm<TpslEditFormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(symbol),
  });

  const {
    fields: tpFields,
    append: appendTp,
    remove: removeTp,
  } = useFieldArray({
    control: form.control,
    name: 'tps',
  });

  const {
    fields: slFields,
    append: appendSl,
    remove: removeSl,
  } = useFieldArray({
    control: form.control,
    name: 'sls',
  });

  const { mutate: updateOrders } = useUpdateOrders();

  const currentPosition = useCurrentAccount((state) => {
    const position = state.positions[symbol];
    if (!position) return;
    return {
      size: position.size,
      isLong: position.isLong,
      cost: position.cost,
      value: position.value.decimal,
    };
  });

  const { sizeDecimals, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );

  const addOrder = (orderType: OrderType.TAKE_PROFIT | OrderType.STOP_LOSS) => {
    if (!currentPosition) return;
    const append = orderType === OrderType.TAKE_PROFIT ? appendTp : appendSl;
    append({
      id: 'NEW',
      symbol,
      isBuy: !currentPosition.isLong,
      size: bigIntToDecimalStr(currentPosition.size, sizeDecimals),
      orderType,
      limitPrice: '',
      reduceOnly: true,
      isSubmitted: false,
    });
  };

  const onSubmit = (data: TpslEditFormSchema) => {
    // Belt and braces: the submit button is already disabled while the weekend
    // pause is on. TP/SL edits go out as cancel + create.
    if (getIsWeekendTradingBlocked(symbol) || getIsMigrationTradingBlocked()) {
      onOpenChange?.(false);
      return;
    }

    const defaultValues = getDefaultValues(symbol);
    const { marketSpec } = useMarketStore(symbol).getState();
    const validatedData = {
      tps: data.tps.map((tp) => ({
        ...tp,
        limitPrice: validateFinalLimitPrice(symbol, tp.limitPrice),
      })),
      sls: data.sls.map((sl) => ({
        ...sl,
        limitPrice: validateFinalLimitPrice(symbol, sl.limitPrice),
      })),
    };
    updateOrders({
      currentOrders: [...defaultValues.tps, ...defaultValues.sls],
      updatedOrders: [...validatedData.tps, ...validatedData.sls],
      marketSpec,
    });
    onOpenChange?.(false);
  };

  return (
    <DialogContent className="gap-0 sm:max-w-3xl">
      <DialogHeader className="px-5 pt-6">
        <DialogTitle className="text-xl text-foreground">
          {t('tpslEdit.title')}
        </DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-3 px-5 py-3">
        <div className="flex h-8 items-center justify-between">
          <p className="text-left text-base text-vestgrey-100">
            {t('tpslEdit.position')}
          </p>
          <p className="font-mono text-vestgrey-50">
            <span
              className={cn(
                currentPosition?.isLong ? 'text-green' : 'text-red',
              )}
            >
              {currentPosition?.isLong
                ? t('tpslEdit.long')
                : t('tpslEdit.short')}
            </span>{' '}
            {formatBigInt(currentPosition?.size, sizeDecimals)}{' '}
            {abbrFromSym(symbol)} (
            {formatNumber(currentPosition?.value, {
              digits: 2,
              style: 'currency',
              round: 'ceil',
            })}
            )
          </p>
        </div>
        <div className="flex h-8 items-center justify-between">
          <p className="text-left text-base text-vestgrey-100">
            {t('tpslEdit.entryPrice')}
          </p>
          <p className="font-mono text-vestgrey-50">
            {currentPosition?.size
              ? formatBigInt(
                  currentPosition.cost / currentPosition.size,
                  priceDecimals,
                )
              : '-'}
          </p>
        </div>
        <div className="flex h-8 items-center justify-between">
          <p className="text-left text-base text-vestgrey-100">
            {t('tpslEdit.midPrice')}
          </p>
          <p className="font-mono text-vestgrey-50">
            <MidPrice symbol={symbol} />
          </p>
        </div>
      </div>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="px-5 pb-6">
            <div className="flex flex-col gap-3 border-y border-border py-3">
              <div className="flex items-center justify-between">
                <h4 className="text-base text-vestgrey-100">
                  {t('tpslEdit.takeProfits')}
                </h4>
                <button
                  onClick={() => addOrder(OrderType.TAKE_PROFIT)}
                  // disabled={!areFieldsValid[OrderType.TAKE_PROFIT]}
                  className="grid h-7 w-7 min-w-7 place-content-center text-vestgrey-100 disabled:opacity-50"
                >
                  <PlusCircledIcon className="h-7 w-7" />
                </button>
              </div>
              {tpFields.map((field, index) => (
                <div key={field.id} className="flex gap-3">
                  <EditTpslInput
                    index={index}
                    orderType={OrderType.TAKE_PROFIT}
                    symbol={symbol}
                  />
                  <button
                    onClick={() => removeTp(index)}
                    className="mt-2 grid h-7 w-7 min-w-7 place-content-center text-vestgrey-100"
                  >
                    <MinusCircledIcon className="h-7 w-7 text-vestgrey-100" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 border-b border-border py-3">
              <div className="flex items-center justify-between">
                <h4 className="text-base text-vestgrey-100">
                  {t('tpslEdit.stopLosses')}
                </h4>
                <button
                  onClick={() => addOrder(OrderType.STOP_LOSS)}
                  // disabled={!areFieldsValid[OrderType.STOP_LOSS]}
                  className="grid h-7 w-7 min-w-7 place-content-center text-vestgrey-100 disabled:opacity-50"
                >
                  <PlusCircledIcon className="h-7 w-7" />
                </button>
              </div>
              {slFields.map((field, index) => (
                <div key={field.id} className="flex gap-3">
                  <EditTpslInput
                    index={index}
                    orderType={OrderType.STOP_LOSS}
                    symbol={symbol}
                  />
                  <button
                    onClick={() => removeSl(index)}
                    className="mt-2 grid h-7 w-7 min-w-7 place-content-center text-vestgrey-100"
                  >
                    <MinusCircledIcon className="h-7 w-7 text-vestgrey-100" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="relative">
            <SubmitButton symbol={symbol} />
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContent>
  );
}

function MidPrice({ symbol }: Pick<TpslEditProps, 'symbol'>) {
  const { midPrice, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      midPrice: state.marketData.midPrice,
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );
  return formatBigInt(midPrice, priceDecimals);
}

function SubmitButton({ symbol }: Pick<TpslEditProps, 'symbol'>) {
  const { t } = useTranslation();
  const form = useFormContext<TpslEditFormSchema>();
  const { isBlocked: isWeekendBlocked, reopenLabel } =
    useWeekendTrading(symbol);
  const { isBlocked: isMigrationBlocked, reason: migrationReason } =
    useMigrationTradingBlock();
  const { currentSize, isLong } = useCurrentAccount((state) => {
    const position = state.positions[symbol];
    return { currentSize: position?.size ?? 0n, isLong: position?.isLong };
  });

  const [tps, sls] = form.watch(['tps', 'sls']);

  const { midPrice, bestBid, bestAsk, priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      midPrice: state.marketData.midPrice,
      bestBid: state.book.bids.at(0)?.at(0),
      bestAsk: state.book.asks.at(0)?.at(0),
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );

  const { isDirty, isValid } = form.formState;

  const areSizesValid = [tps, sls].every(
    (orders) =>
      orders.reduce(
        (prev, curr) => prev + parseDecimalToBigInt(curr.size, sizeDecimals),
        0n,
      ) <= currentSize,
  );

  const isTriggerSideValid = (limitPrice: string, isSl: boolean) => {
    if (!limitPrice || isLong === undefined) return true;
    const triggerPrice = parseDecimalToBigInt(limitPrice, priceDecimals);
    if (triggerPrice <= 0n) return true;
    if (isSl) {
      return isLong
        ? triggerPrice < (bestBid ?? midPrice)
        : triggerPrice > (bestAsk ?? midPrice);
    }
    return isLong ? triggerPrice > midPrice : triggerPrice < midPrice;
  };

  const arePricesValid =
    tps.every((order) => isTriggerSideValid(order.limitPrice, false)) &&
    sls.every((order) => isTriggerSideValid(order.limitPrice, true));

  const areFieldsValid = areSizesValid && arePricesValid;

  const button = (
    <Button
      type="submit"
      className="h-14 w-full rounded-none font-mono text-lg font-normal uppercase tracking-wider"
      disabled={
        !isDirty ||
        !isValid ||
        !areFieldsValid ||
        isWeekendBlocked ||
        isMigrationBlocked
      }
    >
      {isMigrationBlocked
        ? t(getMigrationBlockButtonKey(migrationReason))
        : t('tpslEdit.setTpsl')}
    </Button>
  );

  if (isWeekendBlocked && !isMigrationBlocked) {
    return (
      <div className="w-full">
        <TooltipWrapper
          content={t('marketInfo.marketOpensInTooltip', {
            reopen: reopenLabel,
          })}
        >
          {button}
        </TooltipWrapper>
      </div>
    );
  }

  return button;
}

const getDefaultValues = (symbol: string): TpslEditFormSchema => {
  const { address } = useAccountStore.getState();
  const { orders, newOrderIds } = useOrdersStore.getState().getOrders(address);
  const { marketSpec } = useMarketStore(symbol).getState();
  const tps = Array.from(newOrderIds, (id) => orders[id])
    .filter(
      (order) =>
        order.symbol === symbol && order.orderType === OrderType.TAKE_PROFIT,
    )
    .sort((a, b) => Number(a.limitPrice - b.limitPrice))
    .map((tp) => ({
      // ascending
      id: tp.id,
      symbol: tp.symbol,
      isBuy: tp.isBuy,
      size: bigIntToDecimalStr(tp.size, marketSpec.sizeDecimals),
      orderType: tp.orderType,
      limitPrice: bigIntToDecimalStr(tp.limitPrice, marketSpec.priceDecimals),
      reduceOnly: tp.reduceOnly,
      isSubmitted: true,
      // initMarginRatio: bigIntToDecimalStr(tp.initMarginRatio, 4n),
    }));
  const sls = Array.from(newOrderIds, (id) => orders[id])
    .filter(
      (order) =>
        order.symbol === symbol && order.orderType === OrderType.STOP_LOSS,
    )
    .sort((a, b) => Number(b.limitPrice - a.limitPrice))
    .map((sl) => ({
      // descending
      id: sl.id,
      symbol: sl.symbol,
      isBuy: sl.isBuy,
      size: bigIntToDecimalStr(sl.size, marketSpec.sizeDecimals),
      orderType: sl.orderType,
      limitPrice: bigIntToDecimalStr(sl.limitPrice, marketSpec.priceDecimals),
      reduceOnly: sl.reduceOnly,
      isSubmitted: true,
      // initMarginRatio: bigIntToDecimalStr(sl.initMarginRatio, 4n),
    }));

  return {
    tps,
    sls,
  };
};
